import React, { useState, useEffect } from 'react';
import {
    MessageSquare,
    Search,
    Filter,
    CheckCircle,
    XCircle,
    Trash2,
    RefreshCw,
    Star,
    Copy,
    ExternalLink
} from 'lucide-react';
import { adminApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { useModal } from '../ui/ModalProvider';

const TestimonialManagement = () => {
    const { showConfirm } = useModal();
    const [testimonials, setTestimonials] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const { toast } = useToast();

    useEffect(() => {
        loadTestimonials();
    }, []);

    useEffect(() => {
        filterTestimonials();
    }, [testimonials, searchTerm, statusFilter]);

    const loadTestimonials = async () => {
        try {
            setLoading(true);
            const response = await adminApi.getTestimonials();
            setTestimonials(response.data);
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to load testimonials",
                variant: "destructive"
            });
        } finally {
            setLoading(false);
        }
    };

    const filterTestimonials = () => {
        let result = testimonials;
        if (searchTerm) {
            result = result.filter(t =>
                t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                t.content.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        if (statusFilter) {
            result = result.filter(t => t.status === statusFilter);
        }
        setFiltered(result);
    };

    const updateStatus = async (id, status) => {
        try {
            await adminApi.updateTestimonial(id, { status });
            setTestimonials(prev => prev.map(t => t.id === id ? { ...t, status } : t));
            toast({
                title: "Success",
                description: `Testimonial ${status} successfully`,
            });
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to update testimonial status",
                variant: "destructive"
            });
        }
    };

    const deleteTestimonial = (id) => {
        showConfirm('Delete this testimonial?', async () => {
            try {
                await adminApi.deleteTestimonial(id);
                setTestimonials(prev => prev.filter(t => t.id !== id));
                toast({
                    title: "Deleted",
                    description: "Testimonial removed",
                });
            } catch (error) {
                toast({
                    title: "Error",
                    description: "Failed to delete testimonial",
                    variant: "destructive"
                });
            }
        });
    };

    const copySubmissionLink = () => {
        const uniqueRef = `ref_${Date.now().toString(36)}`;
        const link = `${window.location.origin}/submit-testimonial?ref=${uniqueRef}`;
        navigator.clipboard.writeText(link);
        toast({
            title: "Unique Link Copied",
            description: "Share this unique link with your customers!",
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-gray-600 border-t-silver rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-silver mb-2">Testimonials</h1>
                    <p className="text-gray-400">Review and manage customer feedback</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={copySubmissionLink}
                        className="flex items-center gap-2 px-4 py-2 bg-silver text-black rounded-lg hover:bg-white transition-colors font-medium text-sm"
                    >
                        <Copy size={16} />
                        Copy Submission Link
                    </button>
                    <button
                        onClick={loadTestimonials}
                        className="p-2 bg-gray-800 text-silver rounded-lg hover:bg-gray-700 transition-colors"
                    >
                        <RefreshCw size={20} />
                    </button>
                </div>
            </div>

            <div className="glass-morphism rounded-xl p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search content..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-black/50 border border-gray-600 rounded-lg text-silver focus:border-silver outline-none"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-4 py-2 bg-black/50 border border-gray-600 rounded-lg text-silver outline-none"
                    >
                        <option value="">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                    </select>
                </div>
            </div>

            <div className="grid gap-6">
                {filtered.map((t) => (
                    <div key={t.id} className="glass-morphism rounded-xl p-6 space-y-4">
                        <div className="flex justify-between items-start">
                            <div className="flex gap-4">
                                <div className="w-12 h-12 rounded-full bg-silver/10 flex items-center justify-center text-silver font-bold uppercase">
                                    {t.name.charAt(0)}
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-silver">{t.name}</h3>
                                    <p className="text-sm text-gray-400">{t.company || 'Private Customer'}</p>
                                </div>
                            </div>
                            <div className="flex gap-1 text-yellow-500">
                                {[...Array(t.rating)].map((_, i) => <Star key={i} size={14} fill="currentColor" />)}
                            </div>
                        </div>

                        <p className="text-gray-300 italic">"{t.content}"</p>

                        {t.videoUrl && (
                            <div className="mt-4 rounded-xl overflow-hidden border border-white/10 aspect-video bg-black/40">
                                <video
                                    controls
                                    className="w-full h-full"
                                    src={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8788'}${t.videoUrl}`}
                                />
                            </div>
                        )}

                        <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-white/5">
                            <div className="flex items-center gap-2">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${t.status === 'approved' ? 'bg-green-500/10 text-green-400' :
                                    t.status === 'rejected' ? 'bg-red-500/10 text-red-400' :
                                        'bg-blue-500/10 text-blue-400'
                                    }`}>
                                    {t.status.toUpperCase()}
                                </span>
                                <span className="text-xs text-gray-500">
                                    {new Date(t.createdAt).toLocaleDateString()}
                                </span>
                            </div>

                            <div className="flex gap-2">
                                {t.status !== 'approved' && (
                                    <button
                                        onClick={() => updateStatus(t.id, 'approved')}
                                        className="p-2 text-green-400 hover:bg-green-400/10 rounded-lg transition-colors"
                                        title="Approve"
                                    >
                                        <CheckCircle size={20} />
                                    </button>
                                )}
                                {t.status !== 'rejected' && (
                                    <button
                                        onClick={() => updateStatus(t.id, 'rejected')}
                                        className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                                        title="Reject"
                                    >
                                        <XCircle size={20} />
                                    </button>
                                )}
                                <button
                                    onClick={() => deleteTestimonial(t.id)}
                                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                                    title="Delete"
                                >
                                    <Trash2 size={20} />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}

                {filtered.length === 0 && (
                    <div className="text-center py-20 glass-morphism rounded-xl">
                        <MessageSquare size={48} className="mx-auto text-gray-600 mb-4" />
                        <p className="text-gray-400">No testimonials found.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TestimonialManagement;
